import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { AuthProvider } from "./contexts/authContext.tsx";
import { ThemedApp } from "./components/ThemedApp.tsx";
import {
  withLaunchDarkly,
  LaunchDarklyIdentify,
} from "./contexts/launchDarklyContext.tsx";
import { Toaster } from "./components/ui/sonner.tsx";
import { OAuthDebugGate } from "./components/dev/OAuthDebugOverlay.tsx";
import { MetricsGate } from "./components/MetricsGate.tsx";
import { MetricsIdentify } from "./components/MetricsIdentify.tsx";
import { reloadOnStaleChunk } from "./lib/staleChunkReload.ts";
import { captureOptroReferrer } from "./lib/optroReferrer.ts";
import * as Sentry from "@sentry/react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";

reloadOnStaleChunk();
// Before anything can reload the page to /signin and replace the referrer.
captureOptroReferrer();

const isProduction = import.meta.env.PROD;

if (isProduction) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: 1.0,
  });
}

document.getElementById("spinner-container")!.remove();

// The LaunchDarkly provider wraps the app once at module load. It boots with an
// anonymous context; `LaunchDarklyIdentify` re-identifies with the real
// user/workspace once auth resolves.
const AppWithLaunchDarkly = withLaunchDarkly(() => (
  <ThemedApp>
    <LaunchDarklyIdentify />
    <MetricsGate />
    <MetricsIdentify />
    <App />
    <Toaster closeButton />
    {/* DEV-only diagnostics overlay; off by default, toggled from the sidebar.
        `import.meta.env.DEV` is statically false in every `vite build`, so this
        (and OAuthDebugOverlay + oauthDebugBus) tree-shakes out of production. */}
    {import.meta.env.DEV && <OAuthDebugGate />}
  </ThemedApp>
));

if (isProduction) {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <AuthProvider>
        <AppWithLaunchDarkly />
      </AuthProvider>
    </React.StrictMode>,
  );
} else {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <AuthProvider>
      <AppWithLaunchDarkly />
    </AuthProvider>,
  );
}
